import { useCallback, useMemo, useRef, useState } from "react";
import { slice } from "lodash";

export const usePaginatedResults = props => {
  const {
    results,
    loadMore,
    hasMoreToLoad,
    resultsPerPage,
    totalResultsCount,
    startPage = 1
  } = props;

  const pagesCount = totalResultsCount && Math.ceil(totalResultsCount / resultsPerPage);

  const [currentPage, setCurrentPage] = useState(startPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = Math.min(startIndex + resultsPerPage, totalResultsCount);

  const lastLoadedPage = useRef(currentPage);

  const hasNextPage = hasMoreToLoad || currentPage < pagesCount;
  const hasPreviousPage = currentPage > 1;

  const nextPage = useCallback(() => {
    if (!hasNextPage) return;

    const nextPageIndex = currentPage + 1;

    if (lastLoadedPage.current < nextPageIndex) {
      lastLoadedPage.current = nextPageIndex;
      loadMore();
    }

    setCurrentPage(nextPageIndex);
  }, [currentPage, hasNextPage, loadMore]);

  const previousPage = useCallback(() => {
    if (!hasPreviousPage) return;

    setCurrentPage(currentPage - 1);
  }, [currentPage, hasPreviousPage]);

  const displayedResults = useMemo(
    () => slice(results, startIndex, endIndex),
    [endIndex, results, startIndex]
  );

  const resetPagination = useCallback(() => {
    const newPageIndex = 1;

    lastLoadedPage.current = newPageIndex;
    setCurrentPage(newPageIndex);
  }, []);

  return {
    displayedResults,
    nextPage,
    previousPage,
    resetPagination,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    pagesCount,
    startIndex,
    endIndex,
    totalResultsCount
  };
};
